import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { compressImage } from '../utils/imageCompression';
import * as FiIcons from 'react-icons/fi';

const ICON_OPTIONS = [
  'FiZap', 'FiCode', 'FiLayout', 'FiSearch', 'FiServer', 'FiShield', 'FiSmartphone', 'FiGlobe', 'FiCloud', 'FiTool', 'FiCpu', 'FiMessageSquare',
  'FiActivity', 'FiDatabase', 'FiFileText', 'FiSettings', 'FiEye', 'FiPenTool', 'FiBarChart', 'FiTrash2'
];

export default function ReferenceEditPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const latestReq = useRef(0);

  // Fields
  const [client, setClient] = useState('');
  const [titre, setTitre] = useState('');
  const [slug, setSlug] = useState('');
  const [published, setPublished] = useState(false);
  const [vedette, setVedette] = useState(false); // Champ vedette
  const [inSlider, setInSlider] = useState(false); // Champ in_slider
  const [showClient, setShowClient] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [categories, setCategories] = useState(['web']); // array: infogerance, ingenierie, web
  const [activeIconIdx, setActiveIconIdx] = useState(null); // Which line is choosing an icon?

  // Text areas
  const [resume, setResume] = useState('');
  const [modulesIntro, setModulesIntro] = useState('');
  const [modules, setModules] = useState([]);
  const [interventions, setInterventions] = useState([]);

  // Files
  const [logoFile, setLogoFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [existing, setExisting] = useState(null);
  const logoInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const [logoDragOver, setLogoDragOver] = useState(false);
  const [imagesDragOver, setImagesDragOver] = useState(false);
  const [removedExisting, setRemovedExisting] = useState([]); // filenames to remove
  const [existingImages, setExistingImages] = useState([]); // filenames ordered
  const exDragIndex = useRef(-1);



  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const reqId = ++latestReq.current;
        const rec = await pb.collection('references').getOne(id);
        if (reqId !== latestReq.current) return; // outdated response
        setExisting(rec);
        setClient(rec.client || '');
        setTitre(rec.titre || '');
        setSlug(rec.slug || '');
        setPublished(!!rec.published);
        setVedette(!!rec.vedette); // Init vedette
        setInSlider(!!rec.in_slider); // Init in_slider
        // Default to true if undefined to maintain backward compatibility visual
        setShowClient(rec.show_client !== false);
        setResume(rec.resume || '');
        setModulesIntro(rec.modules_intro || '');
        setModules(Array.isArray(rec.modules) ? rec.modules : []);
        const rawInterv = Array.isArray(rec.interventions) ? rec.interventions : [];
        setInterventions(rawInterv.map(it => (typeof it === 'string' ? { text: it, icon: 'FiZap' } : it)));
        // Handle categories as array or comma-separated string if coming from old field
        let cats = rec.categories || rec.categorie || ['web'];
        if (typeof cats === 'string') cats = [cats];
        setCategories(Array.isArray(cats) ? cats : ['web']);
        setExistingImages(Array.isArray(rec.images) ? rec.images.slice() : []);
      } catch (err) {
        const msg = String(err?.message || '');
        if (/auto-?cancel/i.test(msg)) return; // ignore SDK auto-cancel
        setError(msg || 'Impossible de charger la référence');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      // Compression des fichiers (même logique que aef32: canvas + toBlob)
      let finalLogo = logoFile || null;
      if (logoFile && logoFile.type && logoFile.type.startsWith('image/')) {
        finalLogo = await compressImage(logoFile, { maxWidth: 512, quality: 0.8 });
      }
      let finalImages = imageFiles || [];
      if (imageFiles && imageFiles.length) {
        const compressed = await Promise.all(
          imageFiles.map((f) => (f.type && f.type.startsWith('image/') ? compressImage(f, { maxWidth: 1600, quality: 0.82 }) : Promise.resolve(f)))
        );
        finalImages = compressed;
      }
      const fd = new FormData();
      fd.append('client', client);
      fd.append('titre', titre);
      fd.append('slug', slug || slugify(`${client}-${titre}`));
      fd.append('published', published ? 'true' : 'false');
      fd.append('vedette', vedette ? 'true' : 'false'); // Append vedette
      fd.append('in_slider', inSlider ? 'true' : 'false'); // Append in_slider
      fd.append('show_client', 'true');
      fd.append('show_logo', 'true');
      categories.forEach(cat => fd.append('categorie', cat));

      fd.append('resume', resume);
      fd.append('modules_intro', modulesIntro);
      fd.append('modules', JSON.stringify(modules));
      fd.append('interventions', JSON.stringify(interventions));

      if (finalLogo) fd.append('logo', finalLogo);
      // Conserver les images existantes non supprimées dans l'ordre choisi (drag)
      const keepExisting = Array.isArray(existingImages)
        ? existingImages.filter((name) => !removedExisting.includes(name))
        : [];
      if (keepExisting.length) {
        for (const name of keepExisting) fd.append('images', name);
      }
      if (finalImages && finalImages.length) {
        for (const f of finalImages) fd.append('images', f);
      }

      if (isNew) {
        await pb.collection('references').create(fd);
      } else {
        await pb.collection('references').update(id, fd);
      }
      navigate('/admin/references');
    } catch (err) {
      const msg = String(err?.message || '');
      if (/auto-?cancel/i.test(msg)) return; // ignore SDK auto-cancel noise
      setError(msg || 'Échec de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (cat) => {
    setCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const logoUrl = existing?.logo ? pb.files.getUrl(existing, existing.logo, { thumb: '128x128' }) : null;
  const existingFiles = Array.isArray(existingImages) ? existingImages : [];
  const imageThumbs = existing
    ? existingFiles
      .filter((fn) => !removedExisting.includes(fn))
      .map((fn) => ({ fn, url: pb.files.getUrl(existing, fn, { thumb: '480x320' }) }))
    : [];
  const removeExistingByName = (fn) => {
    setRemovedExisting((arr) => (arr.includes(fn) ? arr : [...arr, fn]));
  };

  // DnD pour réordonner les images existantes (style HeroSlides)
  const onExDragStart = (e, index) => {
    exDragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onExDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = exDragIndex.current;
    if (from === -1 || from === overIndex) return;
    setExistingImages((arr) => {
      const kept = arr.filter((name) => !removedExisting.includes(name));
      // Map kept indexes back to full array positions
      // Build a mapping from kept order to original indices
      const full = arr.slice();
      // We will reorder only the kept subset preserving removed ones in place
      const keptIndices = full
        .map((name, idx) => ({ name, idx }))
        .filter(({ name }) => !removedExisting.includes(name))
        .map(({ idx }) => idx);
      const local = kept.slice();
      const [moved] = local.splice(from, 1);
      local.splice(overIndex, 0, moved);
      // Rebuild full array by replacing keptIndices sequence with local
      const next = full.slice();
      keptIndices.forEach((pos, i) => { next[pos] = local[i]; });
      exDragIndex.current = overIndex;
      return next;
    });
  };
  const onExDrop = () => { exDragIndex.current = -1; };

  return (
    <section className="section">
      <div className="section-inner">
        <div className="section-header">
          <h2>{isNew ? 'Nouvelle référence' : 'Éditer la référence'}</h2>
        </div>

        <div className="card">
          {error ? <div className="alert error">{error}</div> : null}
          {loading ? (
            <div className="admin-table-loading">Chargement…</div>
          ) : (
            <form onSubmit={handleSubmit} className="ref-edit-form">
              <div className="ref-edit-grid">
                <div className="ref-col">
                  {/* Identification */}
                  <div className="admin-structure-group" style={{ marginBottom: '1rem' }}>
                    <h3 className="admin-section-title" style={{ marginBottom: '1rem' }}>Identification</h3>
                    <div className="form-group">
                      <label>Client</label>
                      <input value={client} onChange={(e) => setClient(e.target.value)} required placeholder="Nom du client" />
                    </div>

                    {/* Checkbox Vedette */}
                    <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                      <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400, fontSize: '0.9rem' }}>
                        <input
                          type="checkbox"
                          checked={vedette}
                          onChange={(e) => setVedette(e.target.checked)}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Mettre en vedette (sur l'accueil)</span>
                      </label>
                    </div>
                    {/* Checkbox Slider Confiance */}
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400, fontSize: '0.9rem' }}>
                        <input
                          type="checkbox"
                          checked={inSlider}
                          onChange={(e) => setInSlider(e.target.checked)}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Afficher dans le slider "Confiance"</span>
                      </label>
                    </div>

                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                      <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Catégories de référence (plusieurs choix possibles)</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={categories.includes('infogerance')} onChange={() => toggleCategory('infogerance')} style={{ width: 'auto' }} />
                          <span>Infogérance</span>
                        </label>
                        <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={categories.includes('ingenierie')} onChange={() => toggleCategory('ingenierie')} style={{ width: 'auto' }} />
                          <span>Ingénierie</span>
                        </label>
                        <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={categories.includes('web')} onChange={() => toggleCategory('web')} style={{ width: 'auto' }} />
                          <span>Web</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Titre</label>
                      <input value={titre} onChange={(e) => setTitre(e.target.value)} required placeholder="Titre du projet" />
                    </div>
                    <div className="form-group">
                      <label>Slug (URL)</label>
                      <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Auto-généré si vide" />
                    </div>

                  </div>

                  {/* Logo */}
                  <div className="admin-structure-group">
                    <h3 className="admin-section-title" style={{ marginBottom: '1rem' }}>Logo Client</h3>
                    {logoUrl ? (
                      <div className="ref-logo-preview" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--admin-bg)', borderRadius: '4px', textAlign: 'center' }}>
                        <img src={logoUrl} alt="Logo client" style={{ maxHeight: '80px', maxWidth: '100%' }} />
                      </div>
                    ) : null}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      style={{ display: 'none' }}
                    />
                    <div
                      className={`dropzone ${logoDragOver ? 'dropzone-over' : ''}`}
                      onClick={() => logoInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setLogoDragOver(true); }}
                      onDragEnter={(e) => { e.preventDefault(); setLogoDragOver(true); }}
                      onDragLeave={() => setLogoDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setLogoDragOver(false);
                        const f = e.dataTransfer.files?.[0];
                        if (f && f.type?.startsWith('image/')) setLogoFile(f);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); logoInputRef.current?.click(); } }}
                      aria-label="Déposer ou choisir un logo"
                    >
                      <div className="dropzone-inner">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span>{logoFile ? logoFile.name : 'Choisir un logo'}</span>
                      </div>
                      {logoFile ? (
                        <div className="dropzone-actions" style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setLogoFile(null); }}>Annuler la sélection</button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="ref-col">
                  {/* Description & Contenu */}
                  <div className="admin-structure-group">
                    <h3 className="admin-section-title" style={{ marginBottom: '1rem' }}>Contenu</h3>
                    <div className="form-group">
                      <label>Résumé / Description</label>
                      <textarea rows={5} value={resume} onChange={(e) => setResume(e.target.value)} placeholder="Description principale du projet..." />
                    </div>
                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                      <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.75rem' }}>Modules & Fonctionnalités</label>
                      <label style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.4rem', display: 'block' }}>Introduction / Accroche (Optionnel)</label>
                      <textarea
                        rows={2}
                        value={modulesIntro}
                        onChange={(e) => setModulesIntro(e.target.value)}
                        placeholder="Ex: Le site comporte entre autres..."
                        style={{ marginBottom: '0.75rem' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                        {modules.map((m, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                              value={m}
                              onChange={(e) => {
                                const next = [...modules];
                                next[idx] = e.target.value;
                                setModules(next);
                              }}
                              placeholder={`Module ${idx + 1}`}
                              style={{ flex: 1, margin: 0 }}
                            />
                            <button
                              type="button"
                              className="btn btn-ghost"
                              onClick={() => setModules(modules.filter((_, i) => i !== idx))}
                              style={{ padding: '0.5rem', minWidth: 'auto' }}
                              title="Supprimer ce module"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></svg>
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setModules([...modules, ''])}
                          style={{ border: '1px dashed var(--admin-border)', background: 'var(--admin-bg-alt)', opacity: 0.8 }}
                        >
                          + Ajouter un module
                        </button>
                      </div>
                    </div>

                    <div className="form-group" style={{ position: 'relative' }}>
                      <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.75rem' }}>Nature des Interventions (Icône + Titre)</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {interventions.map((it, idx) => {
                          const item = typeof it === 'string' ? { text: it, icon: 'FiZap' } : it;
                          const ItemIcon = FiIcons[item.icon] || FiIcons.FiZap;
                          return (
                            <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              {/* Icon Selector Button */}
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setActiveIconIdx(activeIconIdx === idx ? null : idx)}
                                style={{ padding: '0.5rem', border: '1px solid var(--admin-border)', minWidth: '40px', background: 'white' }}
                                title="Changer l'icône"
                              >
                                <ItemIcon size={18} />
                              </button>

                              <input
                                value={item.text}
                                onChange={(e) => {
                                  const next = [...interventions];
                                  next[idx] = { ...item, text: e.target.value };
                                  setInterventions(next);
                                }}
                                placeholder={`Intervention ${idx + 1}`}
                                style={{ flex: 1, margin: 0 }}
                              />
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setInterventions(interventions.filter((_, i) => i !== idx))}
                                style={{ padding: '0.5rem', minWidth: 'auto' }}
                                title="Supprimer cette intervention"
                              >
                                <FiIcons.FiTrash2 size={16} />
                              </button>

                              {/* Simple Popover for Icon Grid */}
                              {activeIconIdx === idx && (
                                <div
                                  style={{
                                    position: 'absolute', left: 0, top: '44px', zIndex: 1000,
                                    background: 'white', border: '1px solid var(--admin-border)',
                                    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    padding: '0.75rem', width: '220px',
                                  }}
                                >
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                                    {ICON_OPTIONS.map(iconKey => {
                                      const ChoiceIcon = FiIcons[iconKey];
                                      return (
                                        <button
                                          key={iconKey}
                                          type="button"
                                          className={`btn btn-ghost ${item.icon === iconKey ? 'btn-active' : ''}`}
                                          onClick={() => {
                                            const next = [...interventions];
                                            next[idx] = { ...item, icon: iconKey };
                                            setInterventions(next);
                                            setActiveIconIdx(null);
                                          }}
                                          style={{ padding: '0.5rem', minWidth: 'auto' }}
                                        >
                                          <ChoiceIcon size={16} />
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setInterventions([...interventions, { text: '', icon: 'FiZap' }])}
                          style={{ border: '1px dashed var(--admin-border)', background: 'var(--admin-bg-alt)', opacity: 0.8 }}
                        >
                          + Ajouter une intervention
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Galerie Full Width */}
              <div className="admin-structure-group" style={{ marginTop: '1rem' }}>
                <h3 className="admin-section-title" style={{ marginBottom: '1rem' }}>Galerie Images</h3>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                />
                <div
                  className={`dropzone ${imagesDragOver ? 'dropzone-over' : ''}`}
                  onClick={() => imagesInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setImagesDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setImagesDragOver(true); }}
                  onDragLeave={() => setImagesDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setImagesDragOver(false);
                    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type?.startsWith('image/'));
                    if (files.length) setImageFiles(files);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); imagesInputRef.current?.click(); } }}
                  aria-label="Déposer ou choisir des images"
                  style={{ minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                >
                  <div className="dropzone-inner">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginBottom: '0.5rem' }}>
                      <rect x="3" y="4" width="18" height="14" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                      {imageFiles && imageFiles.length
                        ? `${imageFiles.length} fichier(s) sélectionné(s)`
                        : 'Ajouter des images à la galerie'}
                    </span>
                    <span style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>Glissez-déposez ou cliquez</span>
                  </div>
                  {imageFiles && imageFiles.length ? (
                    <div className="dropzone-actions" style={{ marginTop: '1rem' }}>
                      <button type="button" className="btn btn-ghost" onClick={(e) => { e.stopPropagation(); setImageFiles([]); }}>Effacer la sélection</button>
                    </div>
                  ) : null}
                </div>

                {/* Preview Grid for combined existing + new selection could go here but let's keep it simple as requested: just the grid of existing */}
                {(imageThumbs?.length || imageFiles?.length) ? (
                  <div className="ref-images-grid" style={{ marginTop: '1rem' }}>
                    {imageThumbs.map(({ fn, url }, idx) => (
                      <div
                        key={fn}
                        className="img-cell"
                        style={{ position: 'relative', cursor: 'grab' }}
                        draggable
                        onDragStart={(e) => onExDragStart(e, idx)}
                        onDragOver={(e) => onExDragOver(e, idx)}
                        onDrop={onExDrop}
                        title={`Glissez pour réordonner (position ${idx + 1})`}
                      >
                        <img src={url} alt="Aperçu" />
                        <div className="img-actions" style={{ position: 'absolute', right: 6, top: 6, display: 'flex', gap: 6 }}>
                          <button type="button" className="btn" title="Supprimer" aria-label="Supprimer" onClick={() => removeExistingByName(fn)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    {imageFiles.map((f, idx) => (
                      <div key={`new-${f.name}-${idx}`} className="img-cell" style={{ position: 'relative', border: '2px dashed var(--admin-primary)' }}>
                        <img src={URL.createObjectURL(f)} alt={f.name} />
                        <div className="img-actions" style={{ position: 'absolute', right: 6, top: 6 }}>
                          <button type="button" className="btn" title="Retirer" onClick={() => setImageFiles((arr) => arr.filter((_, i) => i !== idx))}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="form-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <label className="checkbox-row" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
                  <span style={{ fontWeight: 500 }}>Publié sur le site</span>
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => navigate('/admin/references')}>Annuler</button>
                  <button type="submit" className="btn" disabled={saving}>
                    {saving ? (<><span className="btn-spinner" aria-hidden="true" /> Enregistrement…</>) : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function splitLines(s) {
  return String(s || '')
    .split(/\r?\n/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function splitCSV(s) {
  return String(s || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
